import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Users, Sprout, Mail, Sparkles, Clock, FileText, ArrowRight } from "lucide-react";
import NZTerritoryMap from "@/components/NZTerritoryMap";

export default function ConsoleOverview() {
  const [data, setData] = useState(null);
  const [territories, setTerritories] = useState({ regions: [] });

  useEffect(() => {
    api.get("/admin/overview").then(({ data }) => setData(data));
    api.get("/public/territories").then(({ data }) => setTerritories(data));
  }, []);

  if (!data) return <div className="font-serif italic text-[var(--bloom-ink-muted)]">Loading the orchard…</div>;

  return (
    <div data-testid="console-overview">
      <Header title="Overview" subtitle="Everything happening across the Bloom Club, at a glance." />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
        <Kpi label="Active Growers" value={data.growers.active} icon={Sparkles} tone="brand" hint={`${data.growers.total} accounts total`} />
        <Kpi label="Approved" value={data.growers.approved} icon={Sprout} hint="awaiting signing" />
        <Kpi label="In Pipeline" value={data.growers.in_pipeline} icon={FileText} hint="submitted / review" />
        <Kpi label="Subscribers Waitlist" value={data.subscribers.waitlist_total} icon={Mail} hint={`${data.subscribers.invited} invited`} />
      </div>

      {/* Network map */}
      <div className="bloom-card p-6 mb-8" data-testid="admin-overview-map">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-2xl">Network heatmap</h3>
          <span className="font-serif italic text-xs text-[var(--bloom-ink-muted)]">{territories.total_growers || 0} growers across {territories.regions?.length || 0} regions</span>
        </div>
        <NZTerritoryMap
          pins={(territories.regions || []).map((r) => ({ region: r.region, count: r.growers }))}
          size="default"
          showLegend
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12">
        <Card title="Application pipeline">
          <PipelineBar by={data.applications.by_status} total={data.applications.total} />
        </Card>
        <Card title="Subscriber funnel">
          <FunnelRow label="On waitlist" value={data.subscribers.waitlist_total} />
          <FunnelRow label="Invited" value={data.subscribers.invited} accent />
          <FunnelRow label="Pending invite" value={data.subscribers.waitlist_new} muted />
        </Card>
        <Card title="Network health">
          <FunnelRow label="Growers signed up" value={data.growers.total} />
          <FunnelRow label="Reached application" value={data.applications.total} muted />
          <FunnelRow label="Activated (signed)" value={data.growers.active} accent />
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="Recent applications" testid="overview-recent-apps">
          {data.recent_applications.length === 0 ? (
            <Empty>No applications yet.</Empty>
          ) : (
            <ul className="space-y-3">
              {data.recent_applications.map((a) => (
                <li key={a.id} className="flex items-center justify-between gap-3 border-b border-[var(--bloom-border)] pb-3 last:border-0">
                  <div className="min-w-0">
                    <div className="font-serif text-base truncate">{a.applicant_name} <span className="text-[var(--bloom-ink-muted)] text-sm">· {a.farm_name || "·"}</span></div>
                    <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--bloom-ink-muted)]">{a.region} · {new Date(a.submitted_at).toLocaleDateString()}</div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <StatusPill status={a.status} />
                    {a.score != null && <div className="font-serif text-sm mt-1">{a.score}/100</div>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card title="Latest waitlist" testid="overview-recent-waitlist">
          {data.recent_waitlist.length === 0 ? (
            <Empty>No one on the waitlist yet.</Empty>
          ) : (
            <ul className="space-y-3">
              {data.recent_waitlist.map((w) => (
                <li key={w.id} className="flex items-center justify-between gap-3 border-b border-[var(--bloom-border)] pb-3 last:border-0">
                  <div className="min-w-0">
                    <div className="font-serif text-base truncate">{w.name}</div>
                    <div className="text-xs text-[var(--bloom-ink-muted)]">{w.email}</div>
                  </div>
                  <div className="text-right text-[10px] tracking-[0.2em] uppercase text-[var(--bloom-ink-muted)] flex-shrink-0">
                    {w.region || "·"}
                    <div className="mt-1">{new Date(w.created_at).toLocaleDateString()}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}

function Header({ title, subtitle, actions }) {
  return (
    <div className="mb-10 flex items-end justify-between gap-4 flex-wrap">
      <div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--bloom-ink-muted)] mb-1">Heritage Bloom Club</div>
        <h1 className="font-serif text-5xl md:text-6xl leading-none">{title}</h1>
        {subtitle && <p className="font-serif italic text-lg text-[var(--bloom-ink-soft)] mt-3">{subtitle}</p>}
      </div>
      {actions}
    </div>
  );
}

function Kpi({ label, value, icon: Icon, hint, tone }) {
  return (
    <div className="bloom-card p-5" data-testid={`kpi-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="bloom-label">{label}</span>
        <Icon size={14} className={tone === "brand" ? "text-[var(--bloom-accent)]" : "text-[var(--bloom-ink-muted)]"} strokeWidth={1.5} />
      </div>
      <div className={`font-serif text-5xl leading-none ${tone === "brand" ? "text-[var(--bloom-ink)]" : ""}`}>{value}</div>
      {hint && <div className="text-xs text-[var(--bloom-ink-muted)] italic font-serif mt-2">{hint}</div>}
    </div>
  );
}

function Card({ title, children, testid }) {
  return (
    <div className="bloom-card p-6" data-testid={testid}>
      <div className="bloom-label mb-4">{title}</div>
      {children}
    </div>
  );
}

function FunnelRow({ label, value, accent, muted }) {
  return (
    <div className="flex items-baseline justify-between border-b border-[var(--bloom-border)] py-2.5 last:border-0">
      <span className={`font-serif ${muted ? "text-[var(--bloom-ink-muted)]" : ""}`}>{label}</span>
      <span className={`font-serif text-2xl ${accent ? "text-[var(--bloom-ink)]" : ""}`}>{value}</span>
    </div>
  );
}

function PipelineBar({ by, total }) {
  const order = [
    { k: "submitted", l: "Submitted", c: "#7C3F84" },
    { k: "under_review", l: "Review", c: "#C68A52" },
    { k: "more_info_requested", l: "More info", c: "#C68A52" },
    { k: "approved", l: "Approved", c: "#3D1F47" },
    { k: "active", l: "Active", c: "#5B3262" },
    { k: "rejected", l: "Rejected", c: "#8A6A91" },
  ];
  const safe = Math.max(total, 1);
  return (
    <div>
      <div className="flex h-3 overflow-hidden border border-[var(--bloom-border)]" style={{ borderRadius: 999 }}>
        {order.map((seg) => {
          const v = by[seg.k] || 0;
          if (!v) return null;
          return <div key={seg.k} style={{ background: seg.c, width: `${(v / safe) * 100}%` }} />;
        })}
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 mt-4 text-xs">
        {order.map((seg) => (
          <div key={seg.k} className="flex items-center justify-between">
            <span className="flex items-center gap-2"><span style={{ background: seg.c, width: 8, height: 8, borderRadius: 999, display: "inline-block" }} />{seg.l}</span>
            <span className="font-serif">{by[seg.k] || 0}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const c = {
    draft: "text-[var(--bloom-ink-muted)]",
    submitted: "text-[var(--bloom-ink)]",
    under_review: "text-[var(--bloom-warning)]",
    more_info_requested: "text-[var(--bloom-warning)]",
    approved: "text-[var(--bloom-ink)]",
    active: "text-[var(--bloom-ink)]",
    rejected: "text-[var(--bloom-accent)]",
  }[status] || "";
  return <span className={`text-[10px] tracking-[0.2em] uppercase ${c}`}>{(status || "").replace(/_/g, " ")}</span>;
}

function Empty({ children }) {
  return <div className="text-center text-sm text-[var(--bloom-ink-muted)] italic font-serif py-6">{children}</div>;
}
